import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const B2BAuthContext = createContext();

export const useB2BAuth = () => {
  const context = useContext(B2BAuthContext);
  if (!context) {
    throw new Error('useB2BAuth must be used within B2BAuthProvider');
  }
  return context;
};

export const B2BAuthProvider = ({ children }) => {
  const [b2bCustomer, setB2BCustomer] = useState(null);
  const [b2bCustomerType, setB2BCustomerType] = useState(null);
  const [b2bLoading, setB2BLoading] = useState(true);

  useEffect(() => {
    // Check if B2B customer data exists (non-sensitive info only)
    const customerData = localStorage.getItem('b2b_customer');
    const customerTypeData = localStorage.getItem('b2b_customer_type');
    
    if (customerData) {
      try {
        setB2BCustomer(JSON.parse(customerData));
        if (customerTypeData) {
          setB2BCustomerType(JSON.parse(customerTypeData));
        }
      } catch (error) {
        logger.error('Failed to parse B2B customer data:', error);
        localStorage.removeItem('b2b_customer');
        localStorage.removeItem('b2b_customer_type');
      }
    }
    
    setB2BLoading(false);
  }, []);

  const b2bLogin = (token, customer, customerType) => {
    // Token is stored in httpOnly cookie by backend, not in localStorage
    localStorage.setItem('b2b_customer', JSON.stringify(customer));
    localStorage.setItem('b2b_customer_type', JSON.stringify(customerType));
    setB2BCustomer(customer);
    setB2BCustomerType(customerType);
  };

  const b2bLogout = async () => {
    try {
      await axios.post(`${API}/b2b/auth/logout/`);
    } catch (error) {
      logger.error('Logout API call failed:', error);
    }
    localStorage.removeItem('b2b_customer');
    localStorage.removeItem('b2b_customer_type');
    setB2BCustomer(null);
    setB2BCustomerType(null);
  };

  // Get effective B2B discount percentage
  // Priority: custom discount on customer > customer type discount
  const getB2BDiscount = () => {
    if (!b2bCustomer) return 0;
    
    // Check for custom discount on customer
    if (b2bCustomer.custom_discount_percentage != null) {
      return b2bCustomer.custom_discount_percentage;
    }
    
    // Fall back to customer type discount
    if (b2bCustomerType?.discount_percentage != null) {
      return b2bCustomerType.discount_percentage;
    }
    
    return 0;
  };

  const value = {
    b2bCustomer,
    b2bCustomerType,
    b2bLoading,
    b2bLogin,
    b2bLogout,
    isB2BLoggedIn: !!b2bCustomer,
    b2bDiscount: getB2BDiscount()
  };

  return (
    <B2BAuthContext.Provider value={value}>
      {children}
    </B2BAuthContext.Provider>
  );
};
